import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Tenant, TreeItemPicker } from '../../models';

/**
 * Component class for the tree item picker
 */
@Component({
    selector: 'rms-tree-item-picker',
    templateUrl: './tree-item-picker.component.html',
    standalone: false
})
export class TreeItemPickerComponent implements OnInit, OnChanges {
  get items(): Array<TreeItemPicker> {
    return this._items;
  }
  @Input() set items(value: Array<TreeItemPicker>) {
    this._items = value;
  }
  private _items: Array<TreeItemPicker> = []; // raw items list from parent component
  @Input() selectedItems: Array<TreeItemPicker> = []; // raw items list from parent component already selected
  @Input() label: string; // object key to display name in component
  @Input() childLabel: string; // object child key to display in component
  @Input() filterLabel: string; // text label for the search field
  @Input() selectedListLabel: string; // selected items text label
  @Input() btnLabel: string; // button text label
  @Input() searchPlaceholder: string; // search field placeholder
  @Input() childListKey: string; // if list has children, object key for the list
  @Input() columnKey: string;
  @Output() onItemsUpdated = new EventEmitter<Array<TreeItemPicker>>(); // trigger list changes
  private initialItems: Array<TreeItemPicker> = []; // set a default initial list in case of reset list
  public convertedItems: Array<TreeItemPicker> = []; // converted item list to display in component template
  public itemsChecked: Array<TreeItemPicker> = []; // checked items ready to be added
  public itemsAdded: Array<TreeItemPicker> = []; // items already added
  public tenants: Array<Tenant> = [];

  constructor() { }

  ngOnInit() {
    this.convertToLabel();
    this.loadSelected();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes.selectedItems && !changes.selectedItems.firstChange) {
      this.loadSelected();
    }
  }

  /**
   * Resets the list in the component template once the parent component notifies there were changes
   * @param {Array<TreeItemPicker>} items
   */
  protected updateItemsList (items: Array<TreeItemPicker>) {
    this.items = items;
    this.convertToLabel();
  }

  /**
   * Takes a raw list from the parent component and converts it the match the child component template fields
   */
  private convertToLabel () {
    this.convertedItems = [];
    this.items.forEach(item => {
      if (item[this.childListKey] && item[this.childListKey].length > 0) {
        item.children = item[this.childListKey].map(child => child.label = child[this.childLabel]);
      }
      this.convertedItems.push({
        id: item.id,
        companyId: item.companyId,
        label: item[this.label],
        isChecked: item.isChecked,
        expanded: false,
        children: item[this.childListKey],
        columnValue: item[this.columnKey],
        tenant: item.tenant
      });
    });
    this.initialItems = this.convertedItems;
    this.itemsChecked = this.initialItems.filter(subItem => subItem.isChecked);
  }

  private loadSelected () {
    this.itemsAdded = this.selectedItems;
  }

  /**
   * Extract the tenants in the current items
   */
  private getTenantsInItems() {
    const itemTenants = [...new Set(this.items.map(i => i.tenant).filter(t => !!t))];
    const tenantIds = [...new Set(itemTenants.map(t => t.id))];
    this.tenants = tenantIds.map(tId => itemTenants.find(it => it.id === tId)).sort((a, b) => a.id - b.id);
  }

  /**
   * Filters the tree items to display on input changes
   * @param {string} term
   */
  public filter (term: string) {
    if (!term.length) {
      this.convertedItems = this.initialItems;
      return;
    }
    const trimmedTerm = term.replace(/\s/g, '').toLowerCase();
    const matches: Array<TreeItemPicker> = [];

    this.initialItems.forEach(convertedItem => {
      if (convertedItem.label.toLowerCase().includes(trimmedTerm) && !matches.find(item => item.id === convertedItem.id)) {
        matches.push(convertedItem);
      } else if (convertedItem.children && convertedItem.children.length > 0) {
        convertedItem.children.forEach(child => {
          if (child.label.toLowerCase().includes(trimmedTerm) && !matches.find(item => item.id === convertedItem.id)) {
            matches.push(convertedItem);
          }
        });
      }
    });
    this.convertedItems = matches;
  }

  /**
   * Detects if the item us checked/unchecked and adds/removes from the checked list (not the selected list)
   * @param item
   * @param bulkAction: if the actions comes from the bulk selection, add all visible items
   * @param bulkValue
   */
  public itemChanged (item: TreeItemPicker, bulkAction?: boolean, bulkValue?: boolean) {
    if (bulkAction) {
      this.itemsChecked = (bulkValue) ? this.convertedItems : [];
      return;
    }
    if (this.itemsChecked.indexOf(item) <= -1) {
      this.itemsChecked.push(item);
      return;
    }
    this.itemsChecked = this.itemsChecked.filter(i => i.label !== item.label);
  }

  /**
   * Add checked items to the selected list
   * Emits an event to the parent component to be aware of the list change
   */
  public addCheckedItems () {
    this.itemsAdded = this.itemsChecked.slice();
    this.onItemsUpdated.emit(this.itemsAdded);
  }

  /**
   * Removes the item from the selected list
   * @param item: item to remove form the list
   * Emits an event to the parent component to be aware of the list change
   */
  public removeSelectedItem (item) {
    this.itemsAdded = this.itemsAdded.filter(i => i.label !== item.label);
    this.onItemsUpdated.emit(this.itemsAdded);
  }

  /**
   * Shows/hides the sub items if exists
   * @param {TreeItemPicker} item: selected list item
   */
  public toggleCollapse (item: TreeItemPicker) {
    item.expanded = !item.expanded;
  }

}
